/**
 * LivingBrandShowcase — The Heartbeat of Supabank
 *
 * "The red LED is not just a design element — it's the pulse of a living system."
 *
 * Philosophy:
 * - Supabank is alive. It processes transactions 24/7.
 * - The LED blinks every 5 seconds = heartbeat of finance
 * - Gradients breathe in sync = the system is responsive
 * - Together they form the "Living Brand" identity
 *
 * Canon v1.5.0 — December 2025
 */

import React, { useState, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Heart,
  Zap,
  Globe,
  Radio,
  Wifi,
  MessageSquare,
  Sparkles,
} from 'lucide-react';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import {
  SignalDot,
  NewBadge,
  LiveIndicator,
  ProcessingIndicator,
  ActionRequired,
  OnlineStatus,
} from '@/components/ui/signal-dot';

// ============================================
// BRAND CONSTANTS
// ============================================

const BRAND_COLORS = {
  dark: '#050507',
  light: '#F2F4F6',
  ledRed: '#FF2A00',
  ledGlow: 'rgba(255, 42, 0, 0.6)',
  cryptoMint: '#10B77F',
  supabankBlue: '#3B82F6', // Brand Book SSOT
  obsidian: '#0D1F1A',
} as const;

// LED timing constants
const LED_CYCLE_MS = 5000; // 5 seconds full cycle
const LED_BLINK_START = 0.90; // 90% of cycle
const LED_BLINK_DURATION = 0.10; // 10% of cycle (double blink)

// ============================================
// LED HEARTBEAT COMPONENT
// ============================================

interface LEDHeartbeatProps {
  size?: number;
  className?: string;
  showGlow?: boolean;
}

export const LEDHeartbeat: React.FC<LEDHeartbeatProps> = ({
  size = 12,
  className,
  showGlow = true,
}) => {
  return (
    <div className={cn('relative inline-flex items-center justify-center', className)}>
      {/* Glow layer */}
      {showGlow && (
        <div
          className="absolute rounded-full animate-led-glow"
          style={{
            width: size * 3,
            height: size * 3,
            background: `radial-gradient(circle, ${BRAND_COLORS.ledGlow} 0%, transparent 70%)`,
          }}
        />
      )}
      {/* LED dot */}
      <div
        className="rounded-full animate-led-blink"
        style={{
          width: size,
          height: size,
          backgroundColor: BRAND_COLORS.ledRed,
          boxShadow: showGlow ? `0 0 ${size}px ${BRAND_COLORS.ledRed}` : 'none',
        }}
      />
      <style>{`
        @keyframes led-blink {
          0%, 89% { opacity: 0; }
          90% { opacity: 1; }
          93% { opacity: 0; }
          96% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes led-glow {
          0%, 89% { opacity: 0; transform: scale(0.8); }
          90% { opacity: 1; transform: scale(1); }
          93% { opacity: 0; transform: scale(0.9); }
          96% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.8); }
        }
        .animate-led-blink {
          animation: led-blink 5s ease-in-out infinite;
        }
        .animate-led-glow {
          animation: led-glow 5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

// ============================================
// BREATHING GRADIENT COMPONENT
// ============================================

interface BreathingGradientProps {
  variant: 'lavender' | 'blue' | 'mint' | 'supabank';
  className?: string;
  children?: React.ReactNode;
  syncWithLed?: boolean;
}

export const BreathingGradient: React.FC<BreathingGradientProps> = ({
  variant,
  className,
  children,
  syncWithLed = true,
}) => {
  const gradients = {
    lavender: {
      base: 'hsla(270, 95%, 75%, 0.12)',
      accent: 'hsla(217, 91%, 60%, 0.08)',
    },
    blue: {
      base: 'hsla(217, 91%, 60%, 0.10)',
      accent: 'hsla(270, 80%, 65%, 0.08)',
    },
    mint: {
      base: 'hsla(160, 84%, 39%, 0.08)',
      accent: 'hsla(150, 70%, 50%, 0.06)',
    },
    supabank: {
      base: 'hsla(222, 59%, 58%, 0.10)',
      accent: 'hsla(222, 70%, 65%, 0.06)',
    },
  };

  const g = gradients[variant];

  return (
    <div
      className={cn(
        'relative overflow-hidden',
        syncWithLed && 'animate-breathing-gradient',
        className
      )}
      style={{
        background: `
          radial-gradient(ellipse at 30% 30%, ${g.base} 0%, transparent 50%),
          radial-gradient(ellipse at 70% 70%, ${g.accent} 0%, transparent 50%),
          hsl(0, 0%, 100%)
        `,
      }}
    >
      {children}
      <style>{`
        @keyframes breathing-gradient {
          0%, 100% {
            filter: brightness(1);
            transform: scale(1);
          }
          50% {
            filter: brightness(1.02);
            transform: scale(1.001);
          }
          /* Sync pulse with LED at 90% */
          89% { filter: brightness(1); }
          92% { filter: brightness(1.05); }
          95% { filter: brightness(1); }
          98% { filter: brightness(1.03); }
        }
        .animate-breathing-gradient {
          animation: breathing-gradient 5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

// ============================================
// LIVING LOGO COMPONENT
// ============================================

interface LivingLogoProps {
  variant?: 'light' | 'dark';
  size?: 'sm' | 'md' | 'lg';
  showLed?: boolean;
  className?: string;
}

const GLYPH_PATH = 'M 196 48 L 72 112 L 184 144 L 60 208';

export const LivingLogo: React.FC<LivingLogoProps> = ({
  variant = 'dark',
  size = 'md',
  showLed = true,
  className,
}) => {
  const sizes = { sm: 120, md: 180, lg: 240 };
  const svg = sizes[size];
  const stroke = size === 'sm' ? 0.8 : size === 'md' ? 1 : 1.2;

  const isDark = variant === 'dark';

  return (
    <svg
      viewBox="0 0 256 256"
      width={svg}
      height={svg}
      className={cn('overflow-visible', className)}
    >
      <defs>
        <path id="living-glyph" d={GLYPH_PATH} />
        {/* Crypto Mint Glow */}
        <filter id="living-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        {/* LED Glow */}
        <filter id="living-led-glow" x="-200%" y="-200%" width="500%" height="500%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Base Glyph */}
      <use
        href="#living-glyph"
        stroke={isDark ? BRAND_COLORS.supabankBlue : BRAND_COLORS.obsidian}
        strokeWidth={28 * stroke}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ filter: isDark ? 'url(#living-glow)' : 'none' }}
      />

      {/* Crypto Mint Pulse (energy flowing through) */}
      <use
        href="#living-glyph"
        stroke={BRAND_COLORS.cryptoMint}
        strokeWidth={6 * stroke}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="80 600"
        filter={isDark ? 'url(#living-glow)' : 'none'}
        className="animate-energy-flow"
      />

      {/* LED Heartbeat */}
      {showLed && (
        <g className="animate-led-heartbeat">
          <circle
            cx="210"
            cy="220"
            r={4 * stroke}
            fill={BRAND_COLORS.ledRed}
            filter="url(#living-led-glow)"
          />
        </g>
      )}

      {/* Brand Name */}
      <text
        x="128"
        y="248"
        textAnchor="middle"
        fontFamily="'Satoshi', 'Inter', system-ui, sans-serif"
        fontWeight="700"
        fontSize={14 * stroke}
        letterSpacing="0.5"
      >
        <tspan fill={isDark ? '#FFFFFF' : BRAND_COLORS.obsidian}>supa</tspan>
        <tspan fill={BRAND_COLORS.supabankBlue}>bank</tspan>
        <tspan fill={isDark ? 'rgba(255,255,255,0.5)' : 'rgba(13,31,26,0.5)'}>.xyz</tspan>
      </text>

      <style>{`
        @keyframes energy-flow {
          0% { stroke-dashoffset: 680; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { stroke-dashoffset: -680; opacity: 0; }
        }
        .animate-energy-flow {
          animation: energy-flow 3s cubic-bezier(0.4, 0, 0.2, 1) infinite;
        }

        @keyframes led-heartbeat {
          0%, 89% { opacity: 0; transform: scale(0.8); }
          90% { opacity: 1; transform: scale(1.2); }
          92% { opacity: 0.3; transform: scale(1); }
          94% { opacity: 1; transform: scale(1.1); }
          96% { opacity: 0.5; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.8); }
        }
        .animate-led-heartbeat {
          animation: led-heartbeat 5s ease-in-out infinite;
          transform-origin: 210px 220px;
        }
      `}</style>
    </svg>
  );
};

// ============================================
// LIVING STATUS INDICATOR
// ============================================

interface LiveStatusProps {
  status?: 'online' | 'processing' | 'syncing';
  label?: string;
  className?: string;
}

export const LiveStatus: React.FC<LiveStatusProps> = ({
  status = 'online',
  label,
  className,
}) => {
  const statusConfig = {
    online: { label: 'System Online', icon: sfIcons.checkCircle2, color: 'text-green-500' },
    processing: { label: 'Processing', icon: sfIcons.activity, color: 'text-blue-500' },
    syncing: { label: 'Syncing', icon: Radio, color: 'text-amber-500' },
  };

  const config = statusConfig[status];

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <LEDHeartbeat size={8} showGlow={false} />
      <SFIcon icon={config.icon} className={cn('h-4 w-4', config.color)} />
      <span className="text-sm font-medium">{label || config.label}</span>
    </div>
  );
};

// ============================================
// MAIN SHOWCASE COMPONENT
// ============================================

export const LivingBrandShowcase: React.FC<{ className?: string }> = ({ className }) => {
  const [pulseCount, setPulseCount] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setPulseCount((c) => c + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className={cn('space-y-12', className)}>
      {/* Section Header */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Heart className="h-7 w-7 text-primary" />
            <LEDHeartbeat size={8} className="absolute -top-1 -right-1" />
          </div>
          <h2 className={cn(semanticTokens.typography.h2)}>
            Living Brand — The Heartbeat of Supabank
          </h2>
        </div>
        <p className={cn(semanticTokens.typography.body, semanticTokens.fg.muted, 'max-w-3xl')}>
          The red LED is not just a design element — it's the pulse of a living system.
          Every 5 seconds, Supabank shows it's alive, processing transactions 24/7.
          This heartbeat defines our identity.
        </p>
      </div>

      {/* Philosophy Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="relative overflow-hidden">
          <div className="absolute top-4 right-4">
            <LEDHeartbeat size={10} />
          </div>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SFIcon icon={sfIcons.activity} className="h-5 w-5 text-red-500" />
              The Pulse
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              LED blinks every <strong>5 seconds</strong> — the heartbeat of finance.
              It shows the system is alive, not static.
            </p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <SFIcon icon={sfIcons.clock} className="h-3 w-3" />
              <span>Pulse #{pulseCount}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-green-500" />
              The Energy
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Crypto Mint <span className="text-[#10B77F] font-semibold">#10B77F</span> flows
              through the glyph — representing real-time transaction flow.
            </p>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-[#10B77F]" />
              <span className="text-xs font-mono">Crypto Mint</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-500" />
              24/7 Alive
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Unlike static brands, Supabank breathes. The gradients respond,
              the pulse continues — always on, always ready.
            </p>
            <LiveStatus status="online" />
          </CardContent>
        </Card>
      </div>

      {/* Live Demo */}
      <div className="space-y-6">
        <h3 className={cn(semanticTokens.typography.h3)}>Live Demo</h3>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Dark Mode Demo */}
          <div
            className="rounded-2xl p-12 flex flex-col items-center justify-center min-h-[320px] animate-breathing-dark"
            style={{ background: BRAND_COLORS.dark }}
          >
            <LivingLogo variant="dark" size="lg" showLed />
            <div className="mt-8 text-center">
              <LiveStatus status="online" className="text-white" />
            </div>
          </div>

          {/* Light Mode Demo */}
          <BreathingGradient
            variant="lavender"
            className="rounded-2xl border border-border p-12 flex flex-col items-center justify-center min-h-[320px]"
          >
            <LivingLogo variant="light" size="lg" showLed />
            <div className="mt-8 text-center">
              <LiveStatus status="online" />
            </div>
          </BreathingGradient>
        </div>
      </div>

      {/* Gradient + LED Sync */}
      <div className="space-y-6">
        <h3 className={cn(semanticTokens.typography.h3)}>Breathing Gradients + LED Sync</h3>
        <p className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
          Hero gradients subtly "breathe" and pulse in sync with the LED heartbeat.
          Watch the brightness shift at the moment of the blink.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {(['lavender', 'blue', 'mint', 'supabank'] as const).map((variant) => (
            <BreathingGradient
              key={variant}
              variant={variant}
              syncWithLed
              className="rounded-xl border border-border p-8 min-h-[160px] flex flex-col items-center justify-center text-center"
            >
              <LEDHeartbeat size={12} />
              <div className="mt-4 text-sm font-medium capitalize">{variant}</div>
              <div className="text-xs text-muted-foreground">Synced with pulse</div>
            </BreathingGradient>
          ))}
        </div>
      </div>

      {/* Usage Guidelines */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SFIcon icon={sfIcons.security} className="h-5 w-5" />
            Living Brand Guidelines
          </CardTitle>
          <CardDescription>
            How to use the heartbeat system across touchpoints
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h4 className="font-semibold">When to Show LED</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <SFIcon icon={sfIcons.checkCircle2} className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  <span>Hero sections on landing pages</span>
                </li>
                <li className="flex items-start gap-2">
                  <SFIcon icon={sfIcons.checkCircle2} className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  <span>Dashboard headers (shows system status)</span>
                </li>
                <li className="flex items-start gap-2">
                  <SFIcon icon={sfIcons.checkCircle2} className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  <span>Loading states (system is working)</span>
                </li>
                <li className="flex items-start gap-2">
                  <SFIcon icon={sfIcons.checkCircle2} className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  <span>Email headers (brand recognition)</span>
                </li>
              </ul>
            </div>
            <div className="space-y-4">
              <h4 className="font-semibold">When NOT to Show LED</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-red-500">✕</span>
                  <span>Favicon (too small, use glyph only)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500">✕</span>
                  <span>Print materials (animation not possible)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500">✕</span>
                  <span>Error states (could confuse users)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500">✕</span>
                  <span>Multiple logos on same page (visual noise)</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Technical Specs */}
      <div className="p-6 rounded-xl border border-border bg-muted/30">
        <h4 className={cn(semanticTokens.typography.h4, 'mb-4')}>Technical Specifications</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-muted-foreground">LED Color</div>
            <div className="font-mono font-semibold text-[#FF2A00]">#FF2A00</div>
          </div>
          <div>
            <div className="text-muted-foreground">Pulse Cycle</div>
            <div className="font-mono font-semibold">5000ms</div>
          </div>
          <div>
            <div className="text-muted-foreground">Blink Pattern</div>
            <div className="font-mono font-semibold">Double @ 90%</div>
          </div>
          <div>
            <div className="text-muted-foreground">Energy Flow</div>
            <div className="font-mono font-semibold text-[#10B77F]">#10B77F</div>
          </div>
        </div>
      </div>

      {/* LED Communication Language */}
      <div className="space-y-8">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <MessageSquare className="h-6 w-6 text-primary" />
            <h3 className={cn(semanticTokens.typography.h3)}>
              LED Communication Language
            </h3>
          </div>
          <p className={cn(semanticTokens.typography.body, semanticTokens.fg.muted, 'max-w-3xl')}>
            The red LED extends beyond brand identity — it becomes a visual language for
            communicating importance across all touchpoints. Consistent signal patterns help
            users immediately understand context and priority.
          </p>
        </div>

        {/* Signal Types */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-3">
                <SignalDot signal="static" size="lg" color="red" />
                Static
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Always visible. Indicates <strong>active</strong> or <strong>online</strong> state.
              No animation — calm presence.
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-3">
                <SignalDot signal="attention" size="lg" color="red" />
                Attention
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Single blink (2s). Draws eye to <strong>new content</strong> or
              <strong> unread items</strong>. Subtle but noticeable.
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-3">
                <SignalDot signal="important" size="lg" color="red" />
                Important
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Double blink (5s) — matches logo. Signals <strong>important updates</strong> or
              <strong> new features</strong>. Brand-synchronized.
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-3">
                <SignalDot signal="pulse" size="lg" color="mint" glow />
                Pulse
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Continuous gentle pulse. Shows <strong>live data</strong> or
              <strong> real-time connection</strong>. Mint = positive/success.
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-3">
                <SignalDot signal="breathe" size="lg" color="blue" />
                Breathe
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Soft glow breathing. <strong>Processing</strong> or <strong>loading</strong> states.
              Blue = neutral/informational.
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-3">
                <SignalDot signal="urgent" size="lg" color="red" glow />
                Urgent
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Fast urgent blink (0.8s). <strong>Action required now</strong>.
              Use sparingly — only for critical alerts.
            </CardContent>
          </Card>
        </div>

        {/* Compound Components */}
        <div className="space-y-4">
          <h4 className={cn(semanticTokens.typography.h4)}>Ready-to-Use Components</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <NewBadge />
              </div>
              <code className="text-xs text-muted-foreground">&lt;NewBadge /&gt;</code>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <LiveIndicator />
              </div>
              <code className="text-xs text-muted-foreground">&lt;LiveIndicator /&gt;</code>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <ProcessingIndicator />
              </div>
              <code className="text-xs text-muted-foreground">&lt;ProcessingIndicator /&gt;</code>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <OnlineStatus online />
              </div>
              <code className="text-xs text-muted-foreground">&lt;OnlineStatus /&gt;</code>
            </Card>
          </div>
        </div>

        {/* Usage Examples */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Usage Examples by Context
            </CardTitle>
            <CardDescription>
              How to apply signal dots across different touchpoints
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Navigation */}
              <div className="space-y-3">
                <h5 className="font-semibold flex items-center gap-2">
                  <SFIcon icon={sfIcons.notifications} className="h-4 w-4" /> Notifications & Navigation
                </h5>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                    <span>Dashboard</span>
                    <SignalDot signal="important" size="sm" color="red" />
                    <span className="text-xs text-muted-foreground ml-auto">New feature</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                    <span>Messages</span>
                    <SignalDot signal="static" size="sm" color="red" />
                    <span className="text-xs text-muted-foreground ml-auto">Unread</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                    <span>Transactions</span>
                    <SignalDot signal="pulse" size="sm" color="mint" />
                    <span className="text-xs text-muted-foreground ml-auto">Live updates</span>
                  </div>
                </div>
              </div>

              {/* Status & Feedback */}
              <div className="space-y-3">
                <h5 className="font-semibold flex items-center gap-2">
                  <SFIcon icon={sfIcons.activity} className="h-4 w-4" /> Status & Feedback
                </h5>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                    <SignalDot signal="breathe" size="sm" color="blue" />
                    <span>Processing payment...</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                    <SignalDot signal="static" size="sm" color="mint" />
                    <span>Transaction complete</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                    <SignalDot signal="urgent" size="sm" color="red" glow />
                    <span>Verification required</span>
                  </div>
                </div>
              </div>

              {/* Chatbot */}
              <div className="space-y-3">
                <h5 className="font-semibold flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" /> Chatbot & Help
                </h5>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                    <SignalDot signal="breathe" size="sm" color="blue" />
                    <span>AI is thinking...</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                    <SignalDot signal="pulse" size="sm" color="mint" />
                    <span>Agent connected</span>
                  </div>
                  <div className="p-3 rounded-lg border border-border">
                    <div className="flex items-start gap-2">
                      <SignalDot signal="important" size="xs" color="red" className="mt-1" />
                      <span className="text-xs">
                        <strong>Pro tip:</strong> You can link multiple accounts
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="space-y-3">
                <h5 className="font-semibold flex items-center gap-2">
                  <SFIcon icon={sfIcons.mail} className="h-4 w-4" /> Email & Notifications
                </h5>
                <div className="space-y-2 text-sm">
                  <div className="p-3 rounded-lg border border-border bg-white">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-[#FF2A00]" />
                      <span className="font-semibold">Important Update</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Static red dot in email headers signals key information
                    </p>
                  </div>
                  <div className="p-3 rounded-lg border border-border bg-white">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-[#10B77F]" />
                      <span className="font-semibold">Success</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Mint dot for successful transactions
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Semantic Reference */}
        <div className="p-6 rounded-xl border border-border bg-muted/30">
          <h4 className={cn(semanticTokens.typography.h4, 'mb-4')}>Signal Semantics Reference</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div className="space-y-2">
              <div className="font-semibold flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#FF2A00]" />
                Red — Attention
              </div>
              <ul className="space-y-1 text-muted-foreground text-xs">
                <li>• New items requiring attention</li>
                <li>• Important updates (double blink)</li>
                <li>• Urgent actions (fast blink)</li>
                <li>• Unread notifications</li>
              </ul>
            </div>
            <div className="space-y-2">
              <div className="font-semibold flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#10B77F]" />
                Mint — Success/Live
              </div>
              <ul className="space-y-1 text-muted-foreground text-xs">
                <li>• Transaction successful</li>
                <li>• Live data streaming</li>
                <li>• System online status</li>
                <li>• Positive confirmations</li>
              </ul>
            </div>
            <div className="space-y-2">
              <div className="font-semibold flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#3B82F6]" />
                Blue — Processing/Info
              </div>
              <ul className="space-y-1 text-muted-foreground text-xs">
                <li>• Loading/processing states</li>
                <li>• Informational messages</li>
                <li>• Neutral notifications</li>
                <li>• Background operations</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LivingBrandShowcase;
